import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  ICompanySetupPhase,
  ICompanySetupStep,
} from "@/types/(company)/company-setup.interface";

/** `payroll-branch-config` → `payroll_branch_config`, matching the JSON keys. */
const toKey = (value: string) =>
  value
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/**
 * Translates the guide's server-driven copy.
 *
 * Phase labels/blurbs and step titles/descriptions come from the API's static
 * config in English, keyed by stable enum values — so they are looked up in
 * `company:company_setup` by that key, falling back to the API's own string
 * when no translation exists (a step added on the backend still renders, just
 * untranslated, instead of showing a raw key).
 *
 * `step.detail` is NOT covered: the API builds it from live counts ("3 shifts
 * defined…") and only sends the finished English sentence.
 */
export const useSetupCopy = () => {
  const { t } = useTranslation("company");

  const lookup = useCallback(
    (key: string, fallback: string) =>
      t(`company:company_setup.${key}`, { defaultValue: fallback }),
    [t],
  );

  return useMemo(
    () => ({
      phaseLabel: (phase: ICompanySetupPhase) =>
        lookup(`phases.${toKey(phase.key)}.label`, phase.label),
      phaseBlurb: (phase: ICompanySetupPhase) =>
        lookup(`phases.${toKey(phase.key)}.blurb`, phase.blurb),
      stepTitle: (step: Pick<ICompanySetupStep, "key" | "title">) =>
        lookup(`steps.${toKey(step.key)}.title`, step.title),
      stepDescription: (step: ICompanySetupStep) =>
        lookup(`steps.${toKey(step.key)}.description`, step.description),
      trackLabel: (track: string) => lookup(`tracks.${toKey(track)}`, track),
    }),
    [lookup],
  );
};
